import { z } from "zod";
import { apiBuilder, prefixApi } from "@zodios/core";

const attendeeEntrySchema = z.object({
  name: z.string(),
  company: z.string().optional(), // allows missing company
});

const attendeesSchema = z.preprocess((value) => {
  if (Array.isArray(value)) {
    return value;
  }

  if (typeof value === "string") {
    const trimmed = value.trim();
    if (!trimmed) {
      return [];
    }

    if (trimmed.toLowerCase() === "unknown") {
      return [{ name: "Unknown" }];
    }

    return [{ name: trimmed }];
  }

  if (value === null || value === undefined) {
    return [];
  }

  return value;
}, z.array(attendeeEntrySchema));

const transcriptsSchema = z
  .object({
    org_id: z.string(),
    channel_response: z.array(
      z.object({
        companies: z
          .array(
            z.object({
              company_name: z.string(),
              relation_id: z.string(),
            }),
          )
          .nullable(),
        type: z.string(),
        title: z.string(),
        file_id: z.string(),
        meeting_date: z.string(),
        sentiment: z.string(),
        attendees: attendeesSchema,
        call_type: z.string(),
        recording_share_url: z.string().nullable().optional(),
      }),
    ),
  })
  .nullable();
export type TranscriptsSchema = z.infer<typeof transcriptsSchema>;

const transcriptsResponseSchema = z.object({
  response: z
    .array(
      z.object({
        companies: z
          .array(
            z.object({
              company_name: z.string(),
              relation_id: z.string(),
            }),
          )
          .nullable(),
        type: z.string(),
        title: z.string(),
        file_id: z.string(),
        content: z.string(),
        sentiment: z.string().optional(),
        attendees: attendeesSchema.optional(),
        call_type: z.string().optional(),
        recording_share_url: z.string().nullable().optional(),
      }),
    )
    .nullable(),
});

const ScheduleFormSchema = z.object({
  frequency: z.enum(["once", "hourly", "daily", "weekly", "monthly"]).nullable(),
  startAt: z.string().nullable(), // minute fixed to :00 upstream (UI), we just validate ISO
  weekdays: z.array(z.enum(["sun", "mon", "tue", "wed", "thu", "fri", "sat"])).nullable(),
  monthday: z.number().int().min(1).max(31).nullable(),
  interval: z.number().int().min(1).nullable(), // every N
  endCondition: z.enum(["never", "afterDate", "afterRuns"]).nullable(),
  endAt: z.string().nullable(),
  maxRuns: z.number().int().min(1).nullable(),
  enabled: z.boolean(),
});

const taskExecutionStatus = z.enum(["queued", "running", "succeeded", "failed", "canceled"]).or(z.string());
const taskExecution = z.object({
  id: z.number(),
  org: z.number().nullable(),
  status: taskExecutionStatus,
  result: z
    .object({
      file_name: z.string().optional(),
      analysis: z.string().optional().nullable(),
      report: z
        .object({
          title: z.string().optional(),
          content: z.string().optional(),
        })
        .optional(),
    })
    .optional(),
  started_at: z.string().datetime().nullable(),
  completed_at: z.string().datetime().nullable(),
});

const ActionValueSchema = z
  .object({
    value: z.string(),
    description: z.string().optional(),
  })
  .passthrough();

const ActionsSchema = z
  .object({
    name: z.string().optional().default("action"),
    present: z.boolean().optional().default(false),
    allowed_values: z.array(ActionValueSchema).default([]),
  })
  .passthrough();

const MetaSchema = z
  .object({
    // Accept string OR string[]; normalize to a single string
    description: z
      .union([z.string(), z.array(z.string())])
      .optional()
      .transform((v) => (Array.isArray(v) ? v.join("\n") : (v ?? ""))),
    instructions: z
      .union([z.string(), z.array(z.string())])
      .optional()
      .transform((v) => (Array.isArray(v) ? v.join("\n") : (v ?? ""))),

    // Accept string[] OR comma-separated string OR empty string; normalize to string[]
    tags: z
      .union([z.array(z.string()), z.string()])
      .optional()
      .transform((v) => {
        if (Array.isArray(v)) return v;
        if (typeof v === "string") {
          const trimmed = v.trim();
          return trimmed ?
              trimmed
                .split(",")
                .map((s) => s.trim())
                .filter(Boolean)
            : [];
        }
        return [];
      }),

    // May be missing, null, or a full actions object
    actions: z.union([ActionsSchema, z.null()]).optional(),
  })
  .nullable()
  .optional();

const Agent = z
  .object({
    id: z.number(),
    org: z.number().nullable(),
    clio_report_id: z.string().uuid(),
    status: z.string(),
    name: z.string(),
    prompt: z.string(),
    explain: z.string().optional(),
    code: z.string().optional(),
    is_global: z.boolean(),
    created_on: z.string().datetime(),
    updated_on: z.string().datetime(),

    meta: MetaSchema,

    next_run: z.string().nullable(),
    last_run: z
      .object({
        completed_at: z.string().nullable(),
        report_id: z.number(),
      })
      .nullable()
      .optional(),

    schedule: ScheduleFormSchema.nullable(),

    runs: z.array(taskExecution).default([]),
    run_count: z.number().optional(),

    user: z
      .object({
        first_name: z.string().nullable(),
        last_name: z.string().nullable(),
        email: z.string().email().nullable(),
        id: z.number().nullable(),
      })
      .optional(),
  })
  .passthrough(); // allow extra fields

// Paginated envelope (matches your JSON)
const agentListEnvelopeSchema = z.object({
  count: z.number().optional(), // total count of items
  next: z.string().url().nullable().optional(),
  previous: z.string().url().nullable().optional(),
  results: z.array(Agent),
});

export type AgentSchema = z.infer<typeof Agent>;
export type AgentListResponse = z.infer<typeof agentListEnvelopeSchema>;

export type TaskExecution = z.infer<typeof taskExecution>;

const builder = apiBuilder()
  .addEndpoint({
    alias: "getTranscripts",
    method: "get",
    path: "/organizations/:org_id?relation_id=:relation_id&query=:query",
    status: 200,
    parameters: [
      {
        name: "org_id",
        type: "Path",
        schema: z.string(),
        description: "Organization ID",
      },
      {
        name: "relation_id",
        type: "Query",
        schema: z.string().optional(),
        description: "Relation ID",
      },
    ],
    response: transcriptsSchema,
  })
  .addEndpoint({
    alias: "getTranscriptsResponse",
    method: "get",
    path: "/organizations/:org_id/:file_id",
    status: 200,
    parameters: [
      {
        name: "org_id",
        type: "Path",
        schema: z.string(),
        description: "Organization ID",
      },
      {
        name: "file_id",
        type: "Path",
        schema: z.string(),
        description: "File ID",
      },
    ],
    response: transcriptsResponseSchema,
  })
  .addEndpoint({
    alias: "getAgentList",
    method: "get",
    path: "/tasks/definitions/",
    status: 200,
    response: agentListEnvelopeSchema,
  })
  .addEndpoint({
    alias: "getTemplates",
    method: "get",
    path: "/tasks/templates/",
    status: 200,
    response: agentListEnvelopeSchema,
  })
  .addEndpoint({
    alias: "getAgent",
    method: "get",
    path: "/tasks/definitions/:id/",
    status: 200,
    response: Agent,
    parameters: [
      {
        name: "id",
        type: "Path",
        schema: z.number(),
        description: "Agent ID",
      },
    ],
  })
  .addEndpoint({
    alias: "getPlaygroundTasks",
    method: "get",
    path: "/tasks/playground/",
    status: 200,
    response: agentListEnvelopeSchema,
  });

export const transcriptApiDefinitions = prefixApi("/clio", builder.build());
